import { call } from 'redux-saga/effects';
import {
  type RegistrationNumberAlertFormState,
  TravelledVehicleType,
} from '../../../services/update-form/registrationNumberAlert';
import {
  fetchArrivalNotepad as apiFetchArrivalNotepad,
  fetchTravelledVehicle as apiFetchTravelledVehicle,
  fetchVehicleType as apiFetchVehicleType,
} from '../../api';
import type { ArrivalNotepad } from '../../graphql/fragments/gql/ArrivalNotepad';
import type { TravelledVehicle } from '../../graphql/fragments/gql/TravelledVehicle';
import type { VehicleType } from '../../graphql/fragments/gql/VehicleType';
import { trackEvent } from '../../tracking/trackerService';
import { getAppliedDimension } from '../utils/registrationNumberAlertUtil';
import {
  getHeight,
  isFTAndTRMatch,
  shouldApplyTravelledVehicleAutomatically,
} from '../utils/travelledVehicleUtils';

export interface RegistrationNumberAlertStateChange {
  goodsWeight?: number;
  hazardousGoods?: boolean;
  height?: number;
  length?: number;
  loadingListMessage?: string;
  registrationNumberAlert: RegistrationNumberAlertFormState;
  vehicleType?: VehicleType | null;
}

function* fetchArrivalNotepad(
  customerNumber: number,
  routeCode: string,
  vehicleRegNo: string,
  previousGoodsWeight: number | undefined,
  previousHazardousGoods: boolean | undefined,
  previousHeight: number | undefined,
  previousLength: number | undefined,
  previousLoadingListMessage: string | undefined,
): Generator<
  unknown,
  RegistrationNumberAlertStateChange | null,
  ArrivalNotepad | VehicleType | null
> {
  const arrivalNotepadResult = (yield call(
    apiFetchArrivalNotepad,
    customerNumber,
    vehicleRegNo,
    routeCode,
  )) as ArrivalNotepad | null;

  if (!arrivalNotepadResult) {
    return null;
  }

  trackEvent({ category: 'General', action: 'ArrivalNotepad match' });

  const vehicleType = (yield call(
    apiFetchVehicleType,
    customerNumber,
    routeCode,
    arrivalNotepadResult.vehicleTypeCode,
  )) as VehicleType | null;

  if (!vehicleType) {
    // tslint:disable-next-line
    console.log('No vehicle type found for ', arrivalNotepadResult.vehicleTypeCode);
    return null;
  }

  return {
    goodsWeight: arrivalNotepadResult.cargoWeight || previousGoodsWeight,
    hazardousGoods: arrivalNotepadResult.hazardousGoods,
    height: arrivalNotepadResult.height || previousHeight,
    length: arrivalNotepadResult.length || previousLength,
    loadingListMessage: arrivalNotepadResult.note || previousLoadingListMessage,
    registrationNumberAlert: {
      appliedArrivalNotepad: true,
      appliedGoods:
        arrivalNotepadResult.cargoWeight !== previousGoodsWeight &&
        Boolean(arrivalNotepadResult.cargoWeight),
      appliedHazardousGoods: arrivalNotepadResult.hazardousGoods !== previousHazardousGoods,
      appliedHeight: getAppliedDimension(arrivalNotepadResult.height, previousHeight),
      appliedLength: getAppliedDimension(arrivalNotepadResult.length, previousLength),
      appliedLoadingListMessage:
        arrivalNotepadResult.note !== previousLoadingListMessage && !!arrivalNotepadResult.note,
      appliedTravelledVehicle: false,
      result: null,
      travelledVehicleType: TravelledVehicleType.Vehicle,
      vehicleType: null,
    },
    vehicleType,
  };
}

function* fetchTravelledVehicle(
  customerNumber: number,
  routeCode: string,
  vehicleRegNo: string,
  travelledVehicleType: TravelledVehicleType,
  previousVehicleType: VehicleType | null,
  previousHeight: number | undefined,
  previousLength: number | undefined,
): Generator<
  unknown,
  RegistrationNumberAlertStateChange | null,
  TravelledVehicle | VehicleType | null
> {
  const travelledVehicleResult = (yield call(
    apiFetchTravelledVehicle,
    customerNumber,
    vehicleRegNo,
  )) as TravelledVehicle | null;

  if (!travelledVehicleResult) {
    return null;
  }

  trackEvent({ category: 'General', action: 'TravelledVehicle match' });

  const height = getHeight(
    travelledVehicleType,
    Boolean(previousVehicleType?.useTrailerReg),
    previousHeight,
    travelledVehicleResult.height,
  );

  if (travelledVehicleType === TravelledVehicleType.Trailer) {
    return {
      height,
      registrationNumberAlert: {
        appliedArrivalNotepad: false,
        appliedGoods: false,
        appliedHazardousGoods: false,
        appliedHeight: previousHeight !== height,
        appliedLength: false,
        appliedLoadingListMessage: false,
        appliedTravelledVehicle: false,
        result: travelledVehicleResult,
        travelledVehicleType,
        vehicleType: null,
      },
      vehicleType: null,
    };
  }

  if (!shouldApplyTravelledVehicleAutomatically(previousVehicleType, travelledVehicleResult)) {
    // VehicleType mismatch,
    const vehicleType = (yield call(
      apiFetchVehicleType,
      customerNumber,
      routeCode,
      travelledVehicleResult.vehicleTypeCode,
    )) as VehicleType | null;

    return {
      registrationNumberAlert: {
        appliedArrivalNotepad: false,
        appliedGoods: false,
        appliedHazardousGoods: false,
        appliedHeight: false,
        appliedLength: false,
        appliedLoadingListMessage: false,
        appliedTravelledVehicle: false,
        result: travelledVehicleResult,
        travelledVehicleType,
        vehicleType,
      },
    };
  } else {
    // Only dimension affected, apply dimensions if they differ from 0 and previous value.
    const appliedLength = getAppliedDimension(travelledVehicleResult.length, previousLength);

    let vehicleType = null;
    if (isFTAndTRMatch(previousVehicleType, travelledVehicleResult)) {
      vehicleType = (yield call(
        apiFetchVehicleType,
        customerNumber,
        routeCode,
        travelledVehicleResult.vehicleTypeCode,
      )) as VehicleType | null;
    }

    return {
      height,
      length: appliedLength ? travelledVehicleResult.length : previousLength,
      registrationNumberAlert: {
        appliedArrivalNotepad: false,
        appliedGoods: false,
        appliedHazardousGoods: false,
        appliedHeight: previousHeight !== height,
        appliedLength,
        appliedLoadingListMessage: false,
        appliedTravelledVehicle: false,
        result: travelledVehicleResult,
        travelledVehicleType,
        vehicleType,
      },
      vehicleType,
    };
  }
}

export function* handleVehicleRegNoRequest(
  customerNumber: number,
  routeCode: string,
  vehicleRegNo: string,
  travelledVehicleType: TravelledVehicleType,
  previousGoodsWeight: number | undefined,
  previousHazardousGoods: boolean | undefined,
  previousVehicleType: VehicleType | null,
  previousHeight: number | undefined,
  previousLength: number | undefined,
  previousLoadingListMessage: string | undefined,
): Generator<
  unknown,
  RegistrationNumberAlertStateChange | null,
  RegistrationNumberAlertStateChange | null
> {
  if (vehicleRegNo.length < 2) {
    return null;
  }

  const arrivalNotepadChange: RegistrationNumberAlertStateChange | null = yield call(
    fetchArrivalNotepad,
    customerNumber,
    routeCode,
    vehicleRegNo,
    previousGoodsWeight,
    previousHazardousGoods,
    previousHeight,
    previousLength,
    previousLoadingListMessage,
  );

  if (arrivalNotepadChange) {
    return arrivalNotepadChange;
  }

  return yield call(
    fetchTravelledVehicle,
    customerNumber,
    routeCode,
    vehicleRegNo,
    travelledVehicleType,
    previousVehicleType,
    previousHeight,
    previousLength,
  );
}
